import { useState, useEffect } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import Decimal from 'decimal.js'
import PopupContainer from '@/components/PopupContainer'
import PercentageSlider from '@/components/PercentageSlider'
import { accountInfoState } from '@/recoil/AccountInfo'
import { positionDetailValue, collateralInfoValue } from '@/recoil/Position'
import { popupNotificationListState } from '@/recoil/Notification'
import fetcher from '@/api/fetcher'
import './AdjustLeveragePopup.css'

export default function AdjustLeveragePopup({
  show,
  hidePopupCallback = () => {}
}) {
  const [accountInfo] = useRecoilState(accountInfoState)
  const { totalPositionNotionalValue } = useRecoilValue(positionDetailValue)
  const { totalCollateral } = useRecoilValue(collateralInfoValue)
  const setPopupNotification = useSetRecoilState(popupNotificationListState)
  const [isLoading, setIsLoading] = useState(false)
  const [leverageOptionList, setLeverageOptionList] = useState([])
  const [leverage, setLeverage] = useState(0)
  const [percentage, setPercentage] = useState(0)

  useEffect(() => {
    fetcher
      .getLeverageConfig()
      .then(({ available_futures_leverage }) =>
        setLeverageOptionList(available_futures_leverage.split(','))
      )
  }, [show])

  useEffect(() => {
    if (show && accountInfo.max_leverage && leverageOptionList.length > 0) {
      const index = leverageOptionList.find(
        v => Number(v) === Number(accountInfo.max_leverage)
      )
      const basePercentage = Decimal(100).div(
        Decimal(leverageOptionList.length).sub(1)
      )
      setLeverage(accountInfo.max_leverage)
      setPercentage(
        basePercentage.mul(Decimal(index).sub(1)).toFixed(0, Decimal.ROUND_DOWN)
      )
    }
  }, [show, accountInfo.max_leverage, leverageOptionList])

  const marginRatio = Decimal.min(
    10,
    Decimal(totalCollateral).div(totalPositionNotionalValue)
  )
  const currentLeverage = Decimal(1)
    .div(marginRatio)
    .toFixed(2, Decimal.ROUND_CEIL)
  const accountMaxLeverage = Decimal(accountInfo.max_leverage || 0).toFixed(
    2,
    Decimal.ROUND_CEIL
  )

  const onLeverageChange = (percentage, index) => {
    setLeverage(leverageOptionList[index])
    setPercentage(percentage)
  }

  const onConfirmClick = () => {
    if (isLoading) {
      return
    }
    if (accountMaxLeverage === leverage) {
      hidePopupCallback()
      return
    }
    setIsLoading(true)
    fetcher
      .updateLeverageSetting(leverage)
      .then(hidePopupCallback)
      .catch(e => {
        const errorCode = e.response?.data?.code
        const errorMessage = e.response?.data?.message
        errorMessage &&
          setPopupNotification({
            notification: {
              key: Date.now(),
              status: 'error',
              code: errorCode,
              message: errorMessage
            }
          })
      })
      .finally(() => setIsLoading(false))
  }
  return (
    <PopupContainer
      className="adjust-leverage-popup"
      show={show}
      hidePopupCallback={hidePopupCallback}
    >
      <div className="popup-title">Adjust Max Leverage</div>
      <div className="leverage-info">
        <div className="info-row">
          <div>Current Leverage</div>
          <div className="info-value">{currentLeverage}x</div>
        </div>
        <div className="info-row">
          <div>Max Account Leverage</div>
          <div className="info-value">{accountMaxLeverage}x</div>
        </div>
      </div>
      <PercentageSlider
        value={percentage}
        dotAmount={leverageOptionList.length}
        customScaleLabelList={leverageOptionList}
        labelPostfix="x"
        showLabel={true}
        roundToScale={true}
        onUpdate={onLeverageChange}
      />
      <div
        className="main-action-button noselect"
        onClick={() => onConfirmClick()}
      >
        Confirm
      </div>
    </PopupContainer>
  )
}
